import { AnimatePresence, motion } from "framer-motion";

// URL del mapa real de la Comunidad de Madrid (proporcionado por el cliente)
const MAP_OUTLINE =
    "https://customer-assets.emergentagent.com/job_foryou-premium-hub/artifacts/e4jnh58t_madrid-map-spain-region-illustration-vector.jpg";

// Coordenadas reales (lat,lon) calibradas sobre el bbox visible de la imagen.
// Imagen ~1920x1920. BBox dibujo:
//   left  ≈ 215 px (lon -4.58)
//   right ≈ 1815 px (lon -3.05)
//   top   ≈ 110 px (lat 41.17)
//   bottom ≈ 1680 px (lat 39.88)
// Cities reales:
//   Madrid       40.4168, -3.7038
//   Getafe       40.305 , -3.732
//   Parla        40.237 , -3.768
//   Fuenlabrada  40.283 , -3.794
//   → positions in % over the full 1920×1920 image canvas
export const CITY_COORDS = {
    madrid:     { top: "58.1%", left: "57.5%", label: "MADRID" },
    getafe:     { top: "60.6%", left: "57.4%", label: "GETAFE" },
    parla:      { top: "64.8%", left: "55.4%", label: "PARLA" },
    fuenlabrada:{ top: "61.9%", left: "54.0%", label: "FUENLABRADA" },
};

export default function MadridMap({ active, onSelect, cities }) {
    return (
        <div className="relative w-full h-full bg-[#0A0A0A] border border-white/5 overflow-hidden">
            {/* Topography grid */}
            <div
                className="absolute inset-0 opacity-25 pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,94,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,94,0,0.08) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />
            {/* Radial vignette */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 55% 60%, rgba(255,94,0,0.10) 0%, transparent 60%)",
                }}
            />

            {/* Map outline as image — invertimos blanco→negro y teñimos a naranja con drop-shadow gold */}
            <div className="absolute inset-0 p-6 md:p-10 pointer-events-none">
                <div
                    className="w-full h-full bg-no-repeat bg-center bg-contain"
                    style={{
                        backgroundImage: `url('${MAP_OUTLINE}')`,
                        // invert turns white→black and black→white, then we tint
                        filter:
                            "invert(1) brightness(1.2) sepia(0.6) saturate(8) hue-rotate(-15deg) drop-shadow(0 0 18px rgba(255,94,0,0.35))",
                    }}
                />
            </div>

            {/* Pins overlay (positioned with real geographic %) */}
            <div className="absolute inset-0 p-6 md:p-10">
                <div className="relative w-full h-full">
                    {/* Madrid capital reference */}
                    <div
                        style={{
                            top: CITY_COORDS.madrid.top,
                            left: CITY_COORDS.madrid.left,
                        }}
                        className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none"
                    >
                        <span className="block w-2.5 h-2.5 rounded-full bg-brand-gold ring-4 ring-brand-gold/20" />
                        <span className="font-display tracking-[0.3em] text-[10px] text-brand-gold whitespace-nowrap">
                            MADRID
                        </span>
                    </div>

                    {cities.map((c) => {
                        const p = CITY_COORDS[c.id];
                        if (!p) return null;
                        const isActive = active?.id === c.id;
                        return (
                            <button
                                key={c.id}
                                data-testid={`map-pin-${c.id}`}
                                onClick={() => onSelect(c)}
                                style={{ top: p.top, left: p.left }}
                                className="absolute -translate-x-1/2 -translate-y-1/2 group"
                            >
                                {isActive && (
                                    <span className="absolute inset-0 -m-3 rounded-full bg-brand-orange/30 blur-md pulse-dot" />
                                )}
                                <span
                                    className={`relative block rounded-full transition-all duration-300 border-2 border-[#050505] ${
                                        isActive
                                            ? "w-5 h-5 bg-brand-orange scale-110"
                                            : "w-3.5 h-3.5 bg-brand-gold group-hover:bg-brand-orange group-hover:scale-110"
                                    }`}
                                />
                                <span
                                    className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap font-display tracking-[0.25em] text-[10px] px-2 py-1 border transition-all ${
                                        isActive
                                            ? "border-brand-orange text-brand-orange bg-[#050505]"
                                            : "border-white/20 text-brand-text bg-[#050505]/85 group-hover:border-brand-orange group-hover:text-brand-orange"
                                    }`}
                                >
                                    {c.nombre.toUpperCase()}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Corner labels */}
            <div className="absolute top-4 left-5 font-display tracking-[0.35em] text-[10px] text-brand-gold pointer-events-none">
                COMUNIDAD DE MADRID
            </div>
            <div className="absolute top-4 right-5 font-display tracking-[0.35em] text-[10px] text-brand-muted pointer-events-none">
                COBERTURA F YOU
            </div>
            <div className="absolute bottom-4 left-5 font-display tracking-[0.35em] text-[10px] text-brand-muted pointer-events-none">
                3 CENTROS · MADRID SUR
            </div>

            {/* Active info card */}
            <AnimatePresence mode="wait">
                {active && (
                    <motion.div
                        key={active.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="absolute bottom-4 right-5 max-w-xs bg-[#050505]/90 backdrop-blur border-l-2 border-brand-orange p-4 pointer-events-none"
                    >
                        <div className="font-display text-2xl text-brand-text leading-none">
                            {active.nombre.toUpperCase()}
                        </div>
                        <div className="text-xs text-brand-muted mt-1.5">
                            {active.direccion}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
